import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Server, Shield, Clock, AlertTriangle, CheckCircle,
  XCircle, RefreshCw, ChevronDown, ChevronRight, Filter,
  BarChart3, Users, Zap, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(n) { return n?.toLocaleString?.() ?? n ?? '0'; }
function ago(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

const STATUS_BADGE = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failure: 'bg-red-100 text-red-700 border-red-200',
};

const SVC_COLORS = {
  razorpay: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700', icon: '₹' },
  twilio: { bg: 'bg-red-50', border: 'border-red-200', accent: 'text-red-700', icon: 'T' },
  surepass: { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-700', icon: 'S' },
  fast2sms: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', icon: 'F' },
};

export default function SystemMonitorTab() {
  const [hours, setHours] = useState('24');
  const [overview, setOverview] = useState(null);
  const [integrationLogs, setIntegrationLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [errorSummary, setErrorSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [svcFilter, setSvcFilter] = useState('all');
  const [auditFilter, setAuditFilter] = useState('all');
  const [availableActions, setAvailableActions] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, ilRes, alRes, esRes] = await Promise.all([
        fetch(`${API}/api/system-monitor/overview?hours=${hours}`, { headers }),
        fetch(`${API}/api/system-monitor/integration-logs?hours=${hours}&limit=100${svcFilter !== 'all' ? `&service=${svcFilter}` : ''}`, { headers }),
        fetch(`${API}/api/system-monitor/audit-logs?hours=${hours}&limit=100${auditFilter !== 'all' ? `&action=${auditFilter}` : ''}`, { headers }),
        fetch(`${API}/api/system-monitor/error-summary?hours=${hours}`, { headers }),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (ilRes.ok) {
        const d = await ilRes.json();
        setIntegrationLogs(d.logs || []);
      }
      if (alRes.ok) {
        const d = await alRes.json();
        setAuditLogs(d.logs || []);
        setAvailableActions(d.available_actions || []);
      }
      if (esRes.ok) setErrorSummary(await esRes.json());
    } catch (e) { console.error('Monitor fetch error:', e); }
    finally { setLoading(false); }
  }, [hours, svcFilter, auditFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const integrations = overview?.integrations || {};

  return (
    <div className="space-y-6" data-testid="system-monitor-tab">
      {/* Header controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger className="w-36 h-9" data-testid="time-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 1 hour</SelectItem>
              <SelectItem value="6">Last 6 hours</SelectItem>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-monitor">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <div className="flex gap-1 bg-white rounded-lg border p-0.5">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'integrations', label: 'Integrations', icon: Zap },
            { id: 'audit', label: 'Audit Trail', icon: Shield },
            { id: 'errors', label: 'Errors', icon: AlertTriangle },
          ].map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} data-testid={`section-${s.id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${activeSection === s.id ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
              <s.icon className="h-3.5 w-3.5" />{s.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !overview ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : (
        <>
          {/* ── OVERVIEW ── */}
          {activeSection === 'overview' && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard icon={Server} label="Integrations" value={Object.keys(integrations).length} sub="services tracked" color="blue" />
                <SummaryCard icon={Shield} label="Audit Events" value={overview?.audit_events || 0} sub={`last ${hours}h`} color="indigo" />
                <SummaryCard icon={AlertTriangle} label="Backend Errors" value={overview?.backend_errors || 0} sub={`last ${hours}h`} color={overview?.backend_errors > 0 ? 'red' : 'emerald'} />
                <SummaryCard icon={Activity} label="Frontend Crashes" value={overview?.frontend_crashes || 0} sub={`last ${hours}h`} color={overview?.frontend_crashes > 0 ? 'amber' : 'emerald'} />
              </div>

              {/* Integration health cards */}
              {Object.keys(integrations).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Integration Health</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {Object.entries(integrations).map(([svc, data]) => {
                      const c = SVC_COLORS[svc] || { bg: 'bg-gray-50', border: 'border-gray-200', accent: 'text-gray-700', icon: svc[0].toUpperCase() };
                      return (
                        <div key={svc} className={`${c.bg} border ${c.border} rounded-xl p-4`} data-testid={`svc-card-${svc}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-lg ${c.bg} border ${c.border} flex items-center justify-center text-xs font-bold ${c.accent}`}>{c.icon}</div>
                              <span className="text-sm font-semibold text-gray-800 capitalize">{svc}</span>
                            </div>
                            <span className={`text-lg font-bold ${data.success_rate >= 95 ? 'text-emerald-600' : data.success_rate >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                              {data.success_rate}%
                            </span>
                          </div>
                          <div className="flex gap-4 text-[11px] text-gray-500">
                            <span><CheckCircle className="inline h-3 w-3 text-emerald-500 mr-0.5" />{data.success}</span>
                            <span><XCircle className="inline h-3 w-3 text-red-500 mr-0.5" />{data.failure}</span>
                            <span><Clock className="inline h-3 w-3 text-gray-400 mr-0.5" />{data.avg_ms}ms</span>
                          </div>
                          {/* Health bar */}
                          <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${data.success_rate >= 95 ? 'bg-emerald-500' : data.success_rate >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${data.success_rate}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── INTEGRATIONS ── */}
          {activeSection === 'integrations' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-gray-400" />
                <Select value={svcFilter} onValueChange={setSvcFilter}>
                  <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    <SelectItem value="razorpay">Razorpay</SelectItem>
                    <SelectItem value="twilio">Twilio</SelectItem>
                    <SelectItem value="surepass">Surepass</SelectItem>
                    <SelectItem value="fast2sms">Fast2SMS</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-400">{integrationLogs.length} events</span>
              </div>
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b text-left">
                    <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Time</th>
                    <th className="px-3 py-2.5 text-xs text-gray-500 font-medium">Service</th>
                    <th className="px-3 py-2.5 text-xs text-gray-500 font-medium">Operation</th>
                    <th className="px-3 py-2.5 text-xs text-gray-500 font-medium">Status</th>
                    <th className="px-3 py-2.5 text-xs text-gray-500 font-medium text-right">Duration</th>
                    <th className="px-3 py-2.5 text-xs text-gray-500 font-medium">Details</th>
                  </tr></thead>
                  <tbody>
                    {integrationLogs.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No integration events in this period</td></tr>
                    )}
                    {integrationLogs.map((log, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{ago(log.timestamp)}</td>
                        <td className="px-3 py-2.5"><span className="text-xs font-medium capitalize">{log.service}</span></td>
                        <td className="px-3 py-2.5 text-xs text-gray-700">{log.operation}</td>
                        <td className="px-3 py-2.5">
                          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${STATUS_BADGE[log.status] || 'bg-gray-100 text-gray-600'}`}>{log.status}</span>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-right font-mono">{log.duration_ms?.toFixed(0)}ms</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[200px] truncate">
                          {log.error || (log.details ? JSON.stringify(log.details).slice(0, 60) : '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── AUDIT TRAIL ── */}
          {activeSection === 'audit' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-gray-400" />
                <Select value={auditFilter} onValueChange={setAuditFilter}>
                  <SelectTrigger className="w-52 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {availableActions.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-400">{auditLogs.length} events</span>
              </div>
              <div className="space-y-2">
                {auditLogs.length === 0 && (
                  <div className="text-center py-12 text-gray-400 text-sm bg-white rounded-xl border">No audit events in this period</div>
                )}
                {auditLogs.map((log, i) => (
                  <div key={i} className="bg-white rounded-lg border p-3.5 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === i ? null : i)} data-testid={`audit-log-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                          <Shield className="h-4 w-4 text-indigo-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">{log.action?.replace(/_/g, ' ')}</span>
                          <span className="text-xs text-gray-400 ml-2">{log.entity_type} / {log.entity_id?.slice(0, 8)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span><Users className="inline h-3 w-3 mr-0.5" />{log.user_name || log.user_email || 'system'}</span>
                        <span>{ago(log.timestamp)}</span>
                        {expandedLog === i ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </div>
                    </div>
                    {expandedLog === i && (
                      <div className="mt-3 pt-3 border-t text-xs space-y-1.5">
                        {log.changes && Object.entries(log.changes).map(([field, val]) => (
                          <div key={field} className="flex items-center gap-2">
                            <span className="font-medium text-gray-600 w-24">{field}:</span>
                            <span className="text-red-600 line-through">{String(val?.old ?? '-')}</span>
                            <span className="text-gray-400">&rarr;</span>
                            <span className="text-emerald-700 font-medium">{String(val?.new ?? '-')}</span>
                          </div>
                        ))}
                        {log.metadata && (
                          <pre className="bg-gray-50 rounded p-2 text-[10px] text-gray-600 overflow-auto max-h-24">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        )}
                        {log.request_id && <div className="text-gray-400">Request ID: {log.request_id}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ERRORS ── */}
          {activeSection === 'errors' && errorSummary && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* By category */}
                <div className="bg-white rounded-xl border p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Errors by Category</h4>
                  {errorSummary.by_category.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">No errors</p>
                  ) : errorSummary.by_category.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <span className="text-xs text-gray-600">{c._id || 'Unknown'}</span>
                      <span className="text-xs font-bold text-red-600">{c.count}</span>
                    </div>
                  ))}
                </div>
                {/* By service */}
                <div className="bg-white rounded-xl border p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Errors by Service</h4>
                  {errorSummary.by_service.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">No errors</p>
                  ) : errorSummary.by_service.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <span className="text-xs text-gray-600 capitalize">{s._id || 'Unknown'}</span>
                      <span className="text-xs font-bold text-red-600">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Recent errors */}
              <div className="bg-white rounded-xl border p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Errors</h4>
                {errorSummary.recent.length === 0 ? (
                  <div className="text-center py-8 text-emerald-600 text-sm font-medium">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
                    No errors in the last {hours} hours
                  </div>
                ) : (
                  <div className="space-y-2">
                    {errorSummary.recent.map((e, i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${e.severity === 'CRITICAL' ? 'bg-red-500' : e.severity === 'ERROR' ? 'bg-orange-500' : 'bg-yellow-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{e.title}</p>
                          <p className="text-[10px] text-gray-400">{e.service} / {e.category}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 shrink-0">{ago(e.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`rounded-xl border p-4 ${c.split(' ').slice(0, 2).join(' ')}`} data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${c.split(' ')[2]}`} />
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${c.split(' ')[2]}`}>{fmt(value)}</p>
      <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
    </div>
  );
}
